import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useCutoffDay, showDate } from '../utils/months';

const styles = {
  back: { color: '#4a9eff', textDecoration: 'none', fontSize: 13, display: 'inline-block', marginBottom: 12 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  h1: { fontSize: 22, fontWeight: 600 },
  subtitle: { fontSize: 14, color: '#888', marginBottom: 20 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 },
  card: { backgroundColor: '#fff', borderRadius: 8, padding: '16px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  cardLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase' },
  cardValue: { fontSize: 22, fontWeight: 700, marginTop: 2 },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '10px 14px', backgroundColor: '#f8f9fa', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  link: { color: '#4a9eff', textDecoration: 'none' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 500,
    backgroundColor: color === 'green' ? '#e6f9e6' : color === 'amber' ? '#fff8e6' : color === 'red' ? '#fee' : '#f0f0f0',
    color: color === 'green' ? '#27ae60' : color === 'amber' ? '#f39c12' : color === 'red' ? '#e74c3c' : '#666',
  }),
  btn: { padding: '8px 16px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '8px 16px', backgroundColor: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  btnCancel: { padding: '8px 16px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  editSection: { backgroundColor: '#fff', borderRadius: 8, padding: 20, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  select: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  btnRow: { display: 'flex', gap: 8 },
};

const FREQ_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const statusColor = (s) => s === 'active' ? 'green' : s === 'pending_renewal' ? 'amber' : s === 'expired' ? 'red' : 'gray';

export default function TitleDetail() {
  const cutoff = useCutoffDay(); // dates as months when sold by the month
  const { id } = useParams();
  const [title, setTitle] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const [reminderInput, setReminderInput] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg] = useState(null);
  const logoInputRef = useRef(null);

  const load = useCallback(() => {
    Promise.all([
      api.getTitle(id),
      api.getSubscriptions({ title_id: id, limit: 100 }),
    ]).then(([t, subData]) => {
      setTitle(t);
      setForm({ name: t.name, frequency: t.frequency, edition_day: t.edition_day, renewal_lead_days: t.renewal_lead_days || '28', override_company_name: t.override_company_name || '', override_company_address: t.override_company_address || '' });
      setSubscriptions(subData.subscriptions);
      if (t.logo_filename) {
        api.fetchTitleLogo(id).then(url => setLogoUrl(url));
      } else {
        setLogoUrl(null);
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setLogoMsg(null);
    try {
      await api.uploadTitleLogo(id, file);
      const url = await api.fetchTitleLogo(id);
      setLogoUrl(url);
      setLogoMsg({ type: 'success', text: 'Logo uploaded' });
    } catch (err) {
      setLogoMsg({ type: 'error', text: err.message || 'Upload failed' });
    }
    setLogoUploading(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleLogoDelete = async () => {
    setLogoMsg(null);
    try {
      await api.deleteTitleLogo(id);
      setLogoUrl(null);
      setLogoMsg({ type: 'success', text: 'Logo removed' });
    } catch (err) {
      setLogoMsg({ type: 'error', text: err.message || 'Delete failed' });
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    await api.updateTitle(id, form);
    setEditing(false);
    load();
  };

  if (loading) return <div>Loading...</div>;
  if (!title) return <div>Title not found</div>;

  const stats = title.stats || {};

  return (
    <div>
      <Link to="/titles" style={styles.back}>&larr; Back to Titles</Link>

      <div style={styles.header}>
        <h1 style={styles.h1}>{title.name}</h1>
        {!editing && <button style={styles.btnSecondary} onClick={() => setEditing(true)}>Edit</button>}
      </div>

      {editing ? (
        <div style={styles.editSection}>
          <label style={styles.label}>Title Name</label>
          <input style={styles.input} value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus />
          <div style={styles.row}>
            <div>
              <label style={styles.label}>Frequency</label>
              <select style={styles.select} value={form.frequency || 'weekly'} onChange={e => setForm({ ...form, frequency: e.target.value })}>
                {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>Edition Day</label>
              <input style={styles.input} type="number" value={form.edition_day || 1} onChange={e => setForm({ ...form, edition_day: parseInt(e.target.value) || 1 })} />
            </div>
          </div>
          <label style={styles.label}>Reminder Schedule (days before expiry)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {(form.renewal_lead_days || '28').split(',').map(d => d.trim()).filter(Boolean).sort((a, b) => b - a).map(d => (
              <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', backgroundColor: '#e8f0fe', borderRadius: 12, fontSize: 13 }}>
                {d} days
                <span style={{ cursor: 'pointer', color: '#999', fontWeight: 700, fontSize: 14 }} onClick={() => {
                  const days = form.renewal_lead_days.split(',').map(x => x.trim()).filter(x => x !== d);
                  setForm({ ...form, renewal_lead_days: days.length ? days.join(',') : '28' });
                }}>&times;</span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <input style={{ ...styles.input, width: 120, marginBottom: 0 }} type="number" min="1" placeholder="e.g. 7"
              value={reminderInput} onChange={e => setReminderInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const val = parseInt(reminderInput);
                  if (val > 0) {
                    const existing = form.renewal_lead_days.split(',').map(d => d.trim()).filter(Boolean);
                    if (!existing.includes(String(val))) {
                      setForm({ ...form, renewal_lead_days: [...existing, String(val)].join(',') });
                    }
                    setReminderInput('');
                  }
                }
              }}
            />
            <button type="button" style={{ ...styles.btnSecondary, padding: '7px 12px', fontSize: 13 }} onClick={() => {
              const val = parseInt(reminderInput);
              if (val > 0) {
                const existing = form.renewal_lead_days.split(',').map(d => d.trim()).filter(Boolean);
                if (!existing.includes(String(val))) {
                  setForm({ ...form, renewal_lead_days: [...existing, String(val)].join(',') });
                }
                setReminderInput('');
              }
            }}>Add</button>
          </div>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 16 }}>
            Add multiple reminders — e.g. 70, 21, 7 for 10-week, 3-week, and 1-week reminders
          </div>

          <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Company Override</div>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>
              Override the company name and return address for this title. Leave blank to use the default from Settings.
            </div>
            <label style={styles.label}>Company / Trading Name</label>
            <input style={styles.input} value={form.override_company_name || ''} onChange={e => setForm({ ...form, override_company_name: e.target.value })} placeholder="Leave blank to use default" />
            <label style={styles.label}>Return Address</label>
            <textarea style={{ ...styles.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} value={form.override_company_address || ''} onChange={e => setForm({ ...form, override_company_address: e.target.value })} placeholder="Leave blank to use default" />
          </div>

          <div style={styles.btnRow}>
            <button style={styles.btnCancel} onClick={() => { setEditing(false); setReminderInput(''); setForm({ name: title.name, frequency: title.frequency, edition_day: title.edition_day, renewal_lead_days: title.renewal_lead_days || '28', override_company_name: title.override_company_name || '', override_company_address: title.override_company_address || '' }); }}>Cancel</button>
            <button style={styles.btn} onClick={handleSave}>Save</button>
          </div>
        </div>
      ) : (
        <div style={styles.subtitle}>
          {title.frequency} &middot; Reminders: {(title.renewal_lead_days || '28').split(',').map(d => d.trim()).sort((a, b) => b - a).join(', ')} days before expiry
          {title.override_company_name && <span> &middot; Trading as: {title.override_company_name}</span>}
        </div>
      )}

      {/* Logo section */}
      <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Title Logo</div>
        {logoMsg && (
          <div style={{ padding: '6px 12px', borderRadius: 4, fontSize: 13, marginBottom: 10,
            backgroundColor: logoMsg.type === 'success' ? '#e6f9e6' : '#fee',
            color: logoMsg.type === 'success' ? '#27ae60' : '#e74c3c' }}>
            {logoMsg.text}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {logoUrl ? (
            <img src={logoUrl} alt="Title logo" style={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4, padding: 4 }}
              onError={() => setLogoUrl(null)} />
          ) : (
            <div style={{ width: 120, height: 60, border: '1px dashed #ccc', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#999' }}>No logo</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleLogoUpload} style={{ fontSize: 13 }} />
            <div style={{ fontSize: 11, color: '#999' }}>PNG, JPG, GIF or WEBP. Max 2MB.</div>
            {logoUrl && (
              <button onClick={handleLogoDelete} style={{ ...styles.btnSecondary, padding: '4px 12px', fontSize: 12, color: '#e74c3c', borderColor: '#e74c3c', width: 'fit-content' }}>
                Remove Logo
              </button>
            )}
            {logoUploading && <span style={{ fontSize: 12, color: '#888' }}>Uploading...</span>}
          </div>
        </div>
      </div>

      <div style={styles.cards}>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Active</div>
          <div style={{ ...styles.cardValue, color: '#27ae60' }}>{stats.active || 0}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Pending Renewal</div>
          <div style={{ ...styles.cardValue, color: '#f39c12' }}>{stats.pending_renewal || 0}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Expired</div>
          <div style={{ ...styles.cardValue, color: '#e74c3c' }}>{stats.expired || 0}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total</div>
          <div style={styles.cardValue}>{stats.total_subscriptions || 0}</div>
        </div>
      </div>

      <div style={styles.sectionTitle}>Subscribers</div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Subscriber</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>{cutoff ? 'First issue' : 'Start'}</th>
            <th style={styles.th}>{cutoff ? 'Expiry' : 'End'}</th>
          </tr>
        </thead>
        <tbody>
          {subscriptions.map(s => (
            <tr key={s.id}>
              <td style={styles.td}>
                <Link to={`/subscribers/${s.subscriber_id}`} style={styles.link}>{s.subscriber_name}</Link>
              </td>
              <td style={styles.td}><span style={styles.badge(statusColor(s.status))}>{s.status}</span></td>
              <td style={styles.td}>{showDate(s.start_date, cutoff)}</td>
              <td style={styles.td}>{showDate(s.end_date, cutoff)}</td>
            </tr>
          ))}
          {subscriptions.length === 0 && (
            <tr><td style={styles.td} colSpan={4}>No subscriptions yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
